import { useEffect } from "react";
import { AnimatePresence, motion } from "motion/react";
import { HudHeader } from "@/components/shell/HudHeader";
import { PageTransition } from "@/components/shell/PageTransition";
import { RouteErrorBoundary } from "@/components/shell/RouteErrorBoundary";
import { BubbleTooltipOverlay } from "@/components/diagrams/BubbleTooltipOverlay";
import { useAuth } from "@/hooks/use-auth";
import { LoginPage } from "@/pages/LoginPage";
import { useGithubSync } from "@/lib/use-github-sync";
import { useProgress } from "@/stores/useProgress";

/**
 * Persistent shell. The login state and the authenticated state are
 * mounted under a single `<AnimatePresence>` so the swap between them
 * plays as a futurist-monitor signal acquire / shutoff sequence rather
 * than a hard page reload. Both branches inherit the operations-room
 * photo so the backdrop is continuous through the transition; only the
 * brightness, blur, and chrome change.
 *
 * Sequence on a successful login:
 *   1. The LoginPage's exit animation kicks off (`monitor-shutoff`):
 *      the image briefly over-brightens, collapses to a vertical slit,
 *      and goes dark. ~600 ms.
 *   2. The authenticated layout's enter animation runs in parallel
 *      (`monitor-boot`): black → over-bright + slight blur → settles
 *      to rest brightness. The XAES title's existing letter fly-in
 *      animation plays underneath, becoming visible as the monitor
 *      "warms up". ~800 ms.
 *
 * `BubbleTooltipOverlay` is the singleton hover surface used by every
 * `<Bubble>` on every diagram, so it lives inside the authed branch
 * (it shouldn't render on the login screen).
 */
export function AppLayout() {
  const { user } = useAuth();
  // Phase 6 — single mount point for the GitHub polling layer. The
  // hook is a no-op (TanStack query `enabled: false`) when
  // `isGithubSyncEnabled()` returns false, so the no-PAT dev path
  // pays no cost. Mounted here (not per-page) so the 15 s
  // refetchInterval runs for the lifetime of the authenticated
  // session.
  useGithubSync();

  // Hydrate persisted progress overrides once on mount, so the bottom
  // navigator + run-sequence stepper reflect any facilitator-saved
  // status. The static-default fallback makes the pre-hydration frame
  // graceful (it just shows the seed statuses until IDB resolves).
  useEffect(() => {
    void useProgress.getState().hydrate();
  }, []);

  return (
    <div className="relative h-full overflow-hidden">
      {/* No `initial={false}` — the user explicitly asked for a load
          animation on the very first paint of the login page, so we
          let the enter animation play on the initial render. */}
      <AnimatePresence>
        {!user ? (
          <motion.div
            key="login"
            className="absolute inset-0"
            initial={{ opacity: 0, filter: "brightness(0) blur(8px)" }}
            animate={{
              opacity: 1,
              filter: "brightness(1) blur(0px)",
              transition: {
                duration: 0.9,
                ease: [0.22, 1, 0.36, 1],
              },
            }}
            exit={{
              opacity: 0,
              filter: "brightness(2.4) blur(6px)",
              transition: {
                duration: 0.55,
                ease: [0.32, 0, 0.67, 0],
              },
            }}
            style={{ transformOrigin: "center" }}
          >
            <LoginPage />
          </motion.div>
        ) : (
          <motion.div
            key="app"
            className="bg-background text-foreground absolute inset-0 flex flex-col"
            initial={{ opacity: 0, filter: "brightness(0) blur(8px)" }}
            animate={{
              opacity: 1,
              filter: "brightness(1) blur(0px)",
              transition: {
                duration: 0.9,
                delay: 0.15,
                ease: [0.22, 1, 0.36, 1],
              },
            }}
            exit={{
              opacity: 0,
              filter: "brightness(2.4) blur(6px)",
              transition: {
                duration: 0.55,
                ease: [0.32, 0, 0.67, 0],
              },
            }}
          >
            <BubbleTooltipOverlay />
            <HudHeader />
            <main className="flex-1 min-h-0">
              {/* `RouteErrorBoundary` catches throws from any routed
                  page so a broken view degrades to a panel-styled
                  fallback rather than tanking the whole app. The
                  boundary resets on pathname change so users aren't
                  trapped after navigating away. */}
              <RouteErrorBoundary>
                <PageTransition />
              </RouteErrorBoundary>
            </main>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
